import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Loads Microsoft Clarity (session replay + heatmaps) — production web only, and
 * only after Analytics consent is granted.
 *
 * Gated on `environment.clarityProjectId`, which is set ONLY in environment.prod.ts,
 * so it never loads on local, dev, qa, or the native mobile shell (their envs leave
 * the id empty). Injected at runtime rather than pasted into index.html — same
 * rationale as the Meta Pixel — so it ships to exactly one environment.
 *
 * ADMIN EXCLUSION: Clarity is never started on an `/admin/...` route. If the app
 * is currently on an admin route when init() runs, the SDK is not loaded; it
 * starts instead on the first non-admin navigation. (Clarity has no per-route
 * stop, so this only controls where it *starts* — see the note in init().)
 *
 * PRIVACY (this is a payroll app): Clarity records sessions, so it can capture
 * on-screen PII — salaries, NIN, bank details, names. This loader only starts the
 * SDK; the actual protection is the project's masking setting, which MUST be set
 * to "Mask all text" in the Clarity dashboard. Removing the id stops new
 * recording, but sessions already captured persist on Microsoft's side — delete
 * the Clarity project to remove them.
 */
interface ClarityQueue {
  (...args: unknown[]): void;
  q?: unknown[];
}

@Injectable({ providedIn: 'root' })
export class ClarityService {
  private readonly doc    = inject(DOCUMENT);
  private readonly router = inject(Router);
  private started = false;
  private routeSub?: Subscription;

  /** The admin console is excluded from session recording (payroll PII + internal
   *  ops). Matches `/admin` and everything under `/admin/`, ignoring query/hash. */
  private isAdminPath(url: string): boolean {
    const path = (url || '').split(/[?#]/)[0] || '/';
    return path === '/admin' || path.startsWith('/admin/');
  }

  /**
   * Idempotent. No-op unless a Clarity project id is configured (production only).
   *
   * Skips admin routes: if the current page is under `/admin`, Clarity is NOT
   * started now — it starts on the first non-admin navigation instead. Note that
   * because Clarity can't be stopped once running, this controls where recording
   * *starts*, not a per-page pause; navigating public → /admin after it has
   * started will still be recorded. For a hard guarantee that admins are never
   * recorded, also gate init() on `!auth.isSystemAdmin()` (admin routes are
   * SystemAdmin-only), so it never starts for an admin at all.
   */
  init(): void {
    const id = environment.clarityProjectId;
    if (!id || this.started) { return; }

    // Load immediately when we're on a non-admin route…
    if (!this.isAdminPath(this.router.url)) {
      this.load(id);
      return;
    }

    // …otherwise we're on an admin route: defer, and start on the first
    // non-admin navigation. `??=` guards against double-subscribing if init()
    // fires more than once while deferred.
    this.routeSub ??= this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        if (!this.started && !this.isAdminPath(e.urlAfterRedirects)) {
          this.load(id);
        }
      });
  }

  /** Standard Clarity loader: the `window.clarity` command queue + the async tag. */
  private load(projectId: string): void {
    if (this.started) { return; }
    this.started = true;
    this.routeSub?.unsubscribe();

    const win = this.doc.defaultView as (Window & { clarity?: ClarityQueue }) | null;
    if (!win || win.clarity) { return; }

    const clarity = function (this: unknown, ...args: unknown[]): void {
      (clarity.q = clarity.q || []).push(args);
    } as ClarityQueue;
    win.clarity = clarity;

    const script = this.doc.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
    this.doc.head.appendChild(script);
  }
}
